<template>
  <div :class="[$style.wrapper, 'row']">
    <div :data-testid="hoursDataTestId" class="col col-5">
      <bvc-select
        :class="$style.select"
        :label="$t('hours')"
        :options="intervalOptions.hours"
        v-model="duration.hours"
        :height="height"
        :position="position"
        :minWidth="minWidth"
        :disabled="disabled"
        :searchable="false"
      />
    </div>
    <div :data-testid="minutesDataTestId" class="col col-7">
      <bvc-select
        :class="$style.select"
        :label="$t('minutes')"
        :options="intervalOptions.minutes"
        v-model="duration.minutes"
        :height="height"
        :position="position"
        :minWidth="minWidth"
        :disabled="disabled"
        :searchable="false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useVModel } from '@vueuse/core'
import moment from 'moment'

import BvcSelect from '@/booksy-vue-components/components/b-select/index.vue'

defineOptions({
  compatConfig: {
    MODE: 3,
  },
})

const props = defineProps({
  modelValue: {
    type: Number, // minutes
  },

  minutesOptions: {
    type: Array,
  },

  hoursOptions: {
    type: Array,
  },

  formatHoursLabel: {
    type: Function,
  },

  formatMinutesLabel: {
    type: Function,
  },

  height: {
    type: [Number, String],
    default: 'auto',
  },

  position: {
    type: String,
    default: 'bottom-left',
  },

  hoursDataTestId: {
    type: String,
  },
  minutesDataTestId: {
    type: String,
  },

  minWidth: {
    type: Number,
    default: 0,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

const modelValue = useVModel(props, 'modelValue', emit)

const duration = ref({
  minutes: null,
  hours: null,
})

const getFormattedMinutesLabel = (v) => {
  return typeof props.formatMinutesLabel === 'function'
    ? props.formatMinutesLabel(v)
    : `${v} minute(s)`
}

const getFormattedHoursLabel = (v) => {
  return typeof props.formatHoursLabel === 'function'
    ? props.formatHoursLabel(v)
    : `${v} hour(s)`
}

const intervalOptions = computed(() => {
  const hours = props.hoursOptions || []
  const minutes = props.minutesOptions || []

  if (!props.minutesOptions) {
    for (let min = 0; min < 59; min += 5) {
      minutes.push({
        value: min,
        label: getFormattedMinutesLabel(min),
      })
    }
  }

  if (!props.hoursOptions) {
    for (let hour = 0; hour < 24; hour += 1) {
      hours.push({
        value: hour,
        label: getFormattedHoursLabel(hour),
      })
    }
  }

  return {
    minutes,
    hours,
  }
})

watch(
  () => props.modelValue,
  (newValue) => {
    const momentDuration = moment.duration(newValue, 'minutes')

    duration.value = {
      minutes: momentDuration.minutes(),
      hours: momentDuration.hours(),
    }
  },
  { immediate: true }
)

watch(
  duration,
  () => {
    const momentDuration = moment.duration(duration.value.hours, 'hours')
    momentDuration.add(duration.value.minutes, 'minutes')

    modelValue.value = momentDuration.as('minutes')
    emit('change')
  },
  { deep: true }
)
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
}

.select {
  &:last-of-type {
    margin-right: 0;
  }
}
</style>
